import { ComponentType, ConnectionPositionPair, FlexibleConnectedPositionStrategyOrigin, Overlay } from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { inject, Injectable, Injector, TemplateRef, ViewContainerRef } from '@angular/core';

import { POPOVER_DATA } from '@treetop/shared/model';

import { PopoverConfig } from './popover-config';
import { PopoverRef } from './popover-ref';

const defaultConfig: PopoverConfig<null> = {
  hasBackdrop: true,
  backdropClass: '',
  disableClose: false,
  panelClass: '',
  arrowOffset: 30,
  offsetY: 0,
  positionStrategy: 'normal',
  ignoreElement: undefined,
};

@Injectable({
  providedIn: 'root',
})
export class PopoverService {
  private _overlay = inject(Overlay);

  public open<DATA = any, RETURN = any>(
    componentOrTemplate: ComponentType<any> | TemplateRef<any>,
    target: FlexibleConnectedPositionStrategyOrigin,
    config?: Partial<PopoverConfig<DATA>>,
  ): PopoverRef<RETURN>;

  public open<DATA = any, RETURN = any>(
    componentOrTemplate: ComponentType<any> | TemplateRef<any>,
    target: FlexibleConnectedPositionStrategyOrigin,
    config?: Partial<PopoverConfig<DATA>>,
    viewContainerRef?: ViewContainerRef,
  ): PopoverRef<RETURN>;

  public open<RETURN = any>(
    componentOrTemplate: ComponentType<any> | TemplateRef<any>,
    target: FlexibleConnectedPositionStrategyOrigin,
    config?: Partial<PopoverConfig<any>>,
    viewContainerRef?: ViewContainerRef,
  ): PopoverRef<RETURN>;

  public open<DATA = any, RETURN = any>(
    componentOrTemplate: ComponentType<any> | TemplateRef<any>,
    target: FlexibleConnectedPositionStrategyOrigin,
    config: Partial<PopoverConfig<DATA>> = {},
    viewContainerRef?: ViewContainerRef,
  ): PopoverRef<RETURN> {
    const popoverConfig: PopoverConfig<DATA> = { ...defaultConfig, ...config };

    const offsetY = popoverConfig.offsetY;

    let positions: ConnectionPositionPair[] = [
      // bottom right
      {
        overlayX: 'start',
        overlayY: 'top',
        originX: 'end',
        originY: 'bottom',
        panelClass: ['top', 'start'],
        offsetY,
      },
      // bottom center
      {
        overlayX: 'center',
        overlayY: 'top',
        originX: 'center',
        originY: 'bottom',
        panelClass: ['top', 'center'],
      },
      // bottom left
      {
        overlayX: 'end',
        overlayY: 'top',
        originX: 'end',
        originY: 'bottom',
        panelClass: ['top', 'end'],
        offsetY,
      },
      // top right
      {
        overlayX: 'start',
        overlayY: 'bottom',
        originX: 'start',
        originY: 'top',
        panelClass: ['bottom', 'start'],
        offsetY: -1 * offsetY,
      },
      // top left
      {
        overlayX: 'end',
        overlayY: 'bottom',
        originX: 'end',
        originY: 'top',
        panelClass: ['bottom', 'end'],
        offsetY: -1 * offsetY,
      },
    ];

    switch (popoverConfig.positionStrategy) {
      case 'center':
        positions = [
          {
            overlayX: 'center',
            overlayY: 'top',
            originX: 'center',
            originY: 'bottom',
            panelClass: ['top', 'center'],
            offsetY,
          },
          ...positions,
        ];
        break;
      case 'top-center':
        positions = [
          {
            overlayX: 'center',
            overlayY: 'bottom',
            originX: 'center',
            originY: 'top',
            panelClass: ['bottom', 'center'],
            offsetY: -1 * offsetY,
          },
          ...positions,
        ];
        break;

      case 'start':
        positions = [
          {
            overlayX: 'end',
            overlayY: 'center',
            originX: 'start',
            originY: 'center',
            panelClass: ['top', 'center'],
            offsetY,
          },
          ...positions,
        ];
        break;

      case 'right':
        positions = [
          {
            overlayX: 'start',
            overlayY: 'center',
            originX: 'end',
            originY: 'center',
            panelClass: ['top', 'center'],
            offsetY,
          },
          ...positions,
        ];
        break;
      case 'top-right':
        positions = [
          {
            overlayX: 'start',
            overlayY: 'top',
            originX: 'end',
            originY: 'top',
            panelClass: ['top', 'start'],
            offsetY,
          },
          ...positions,
        ];
        break;
      case 'bottom-right':
        positions = [
          {
            overlayX: 'end',
            overlayY: 'top',
            originX: 'end',
            originY: 'bottom',
            offsetY,
          },
          ...positions,
        ];
        break;
    }

    const positionStrategy = this._overlay.position().flexibleConnectedTo(target).withFlexibleDimensions(true).withPositions(positions);

    const scrollStrategy =
      popoverConfig.scrollStrategy === 'close' ? this._overlay.scrollStrategies.close() : this._overlay.scrollStrategies.reposition();

    const overlayRef = this._overlay.create({
      ...popoverConfig,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      positionStrategy,
      scrollStrategy,
    });

    const popoverRef = new PopoverRef<RETURN>(overlayRef, popoverConfig);

    if (!overlayRef.getConfig().hasBackdrop) {
      overlayRef.outsidePointerEvents().subscribe((event: MouseEvent) => {
        const ignoreElement = popoverConfig.ignoreElement;
        if (ignoreElement && event.target instanceof Node && ignoreElement.contains(event.target)) {
          return;
        }

        popoverRef.close();
      });
    }

    // Check if the input is a component or a template
    if (componentOrTemplate instanceof TemplateRef) {
      if (!viewContainerRef) {
        throw new Error('ViewContainerRef is required when using TemplateRef');
      }

      // Create a template portal
      const templatePortal = new TemplatePortal(componentOrTemplate, viewContainerRef, {
        $implicit: config.data,
        popoverRef,
      });

      overlayRef.attach(templatePortal);
    } else {
      // Handle component portal as before
      overlayRef.attach(
        new ComponentPortal(
          componentOrTemplate,
          null,
          Injector.create({
            providers: [
              { provide: POPOVER_DATA, useValue: config.data },
              { provide: PopoverRef, useValue: popoverRef },
              ...(config.providers ?? []),
            ],
          }),
        ),
      );
    }

    return popoverRef;
  }
}
